export interface CountdownController {
  start: () => void;
  stop: () => void;
  getRemainingMs: () => number;
}

export interface CreateCountdownParams {
  expiresAt: string;
  onTick: (remainingMs: number) => void;
  onExpire: () => void;
  tickMs?: number;
}

function parseExpiresAt(expiresAt: string): number {
  const hasTimezone = /(?:z|[+-]\d{2}:?\d{2})$/i.test(expiresAt.trim());
  const isoLike = expiresAt.includes('T') ? expiresAt : expiresAt.replace(' ', 'T');
  const normalized = hasTimezone ? isoLike : `${isoLike}Z`;

  const timestamp = new Date(normalized).getTime();

  if (!Number.isFinite(timestamp)) {
    throw new Error(`Invalid expiresAt value: ${expiresAt}`);
  }

  return timestamp;
}

export function createCountdown(params: CreateCountdownParams): CountdownController {
  const tickMs = params.tickMs ?? 1000;
  const expiresAtTimestamp = parseExpiresAt(params.expiresAt);

  let timerId: number | null = null;
  let expired = false;

  const getRemainingMs = (): number => Math.max(0, expiresAtTimestamp - Date.now());

  const tick = (): void => {
    const remainingMs = getRemainingMs();

    params.onTick(remainingMs);

    if (remainingMs <= 0 && !expired) {
      expired = true;
      params.onExpire();

      if (timerId !== null) {
        window.clearInterval(timerId);
        timerId = null;
      }
    }
  };

  return {
    start() {
      if (timerId !== null) {
        return;
      }

      tick();
      timerId = window.setInterval(tick, tickMs);
    },

    stop() {
      if (timerId !== null) {
        window.clearInterval(timerId);
        timerId = null;
      }
    },

    getRemainingMs
  };
}